/**
 * Shared store persistence utilities
 * Common debounced localStorage persistence pattern used by multiple stores
 */

export interface DebouncedPersistence<T> {
  save: (data: T) => void;
  saveImmediate: (data: T) => void;
}

/**
 * Creates a debounced persistence handler for localStorage.
 * Uses a hash function to avoid unnecessary writes.
 *
 * @param storageKey - localStorage key
 * @param serialize - converts data to a JSON-storable string
 * @param computeHash - fast hash for change detection (avoids JSON.stringify on every update)
 * @param debounceMs - debounce interval in ms (default: 500)
 */
export function createDebouncedPersistence<T>(
  storageKey: string,
  serialize: (data: T) => string,
  computeHash: (data: T) => string,
  debounceMs: number = 500,
): DebouncedPersistence<T> {
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastHash = '';
  let lastSavedHash = '';

  function save(data: T): void {
    const currentHash = computeHash(data);
    if (currentHash === lastHash) return;
    lastHash = currentHash;

    if (currentHash !== lastSavedHash) {
      if (saveTimeout) clearTimeout(saveTimeout);
      saveTimeout = setTimeout(() => {
        try {
          localStorage.setItem(storageKey, serialize(data));
        } catch {}
        lastSavedHash = currentHash;
      }, debounceMs);
    }
  }

  function saveImmediate(data: T): void {
    try {
      localStorage.setItem(storageKey, serialize(data));
    } catch {}
    lastHash = computeHash(data);
    lastSavedHash = lastHash;
  }

  return { save, saveImmediate };
}
